// Document service with localStorage + IndexedDB persistence

import * as imageStore from './imageStore';

const STORAGE_KEY = 'geppro_documents';

// Utility: load documents from localStorage
function loadDocuments() {
  try {
    const json = localStorage.getItem(STORAGE_KEY);
    return json ? JSON.parse(json) : [];
  } catch {
    return [];
  }
}

// Utility: save documents to localStorage
function saveDocuments(docs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(docs));
  } catch (e) {
    console.error('Failed to save documents:', e);
  }
}

// Utility: serialize attachments (save files to IndexedDB, return keys)
async function serializeAttachments(attachments) {
  if (!Array.isArray(attachments)) return [];
  
  const serialized = [];
  for (const att of attachments) {
    if (att.file) {
      // Save file to IndexedDB and get key
      const key = await imageStore.saveImage(att.file);
      serialized.push({
        id: att.id,
        name: att.name,
        type: att.type,
        key: key
      });
    } else if (att.key) {
      // Already serialized, keep as is
      serialized.push({
        id: att.id,
        name: att.name,
        type: att.type,
        key: att.key
      });
    }
  }
  return serialized;
}

const documentService = {
  // Fetch all documents
  async fetchDocuments() {
    return new Promise(resolve => {
      setTimeout(() => {
        const docs = loadDocuments();
        resolve(docs);
      }, 100);
    });
  },

  // Create a new document
  async createDocument(payload) {
    return new Promise(async resolve => {
      setTimeout(async () => {
        const docs = loadDocuments();
        
        // Serialize attachments
        const serializedAttachments = await serializeAttachments(payload.attachments);
        
        const newDoc = {
          id: Date.now(),
          title: payload.title,
          description: payload.description,
          tipo: payload.tipo,
          textContent: payload.textContent || '',
          attachments: serializedAttachments,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        docs.push(newDoc);
        saveDocuments(docs);
        resolve(newDoc);
      }, 200);
    });
  },

  // Update an existing document
  async updateDocument(id, payload) {
    return new Promise(async resolve => {
      setTimeout(async () => {
        const docs = loadDocuments();
        const index = docs.findIndex(d => d.id === id);
        
        if (index === -1) return resolve(null);
        
        // Delete old attachment files that are no longer present
        const oldAttachments = docs[index].attachments || [];
        const newAttachmentKeys = (payload.attachments || [])
          .map(a => a.key)
          .filter(Boolean);
        
        for (const oldAtt of oldAttachments) {
          if (oldAtt.key && !newAttachmentKeys.includes(oldAtt.key)) {
            try {
              await imageStore.deleteImage(oldAtt.key);
            } catch {}
          }
        }
        
        // Serialize new attachments
        const serializedAttachments = await serializeAttachments(payload.attachments);
        
        docs[index] = {
          ...docs[index],
          title: payload.title,
          description: payload.description,
          tipo: payload.tipo,
          textContent: payload.textContent || '',
          attachments: serializedAttachments,
          updatedAt: new Date().toISOString()
        };
        
        saveDocuments(docs);
        resolve(docs[index]);
      }, 200);
    });
  },

  // Delete a document
  async deleteDocument(id) {
    return new Promise(async resolve => {
      setTimeout(async () => {
        const docs = loadDocuments();
        const doc = docs.find(d => d.id === id);
        
        if (doc && doc.attachments) {
          // Delete all attachment files from IndexedDB
          for (const att of doc.attachments) {
            if (att.key) {
              try {
                await imageStore.deleteImage(att.key);
              } catch {}
            }
          }
        }
        
        const filtered = docs.filter(d => d.id !== id);
        saveDocuments(filtered);
        resolve({ success: true });
      }, 200);
    });
  },

  // Get attachment blob/url for download
  async getAttachmentBlob(key) {
    return imageStore.getImageBlob(key);
  }
};

export default documentService;
